/* * Version: 3.6.0
 * Letzte Änderung: 16.09.2026, 14:45
 * Änderungshistorie: Funktion exportSingleClassificationToPDF hinzugefügt und Druck-Button (PDF) neben dem Stream-Button für jede einzelne Wertung integriert.
 * Copyright: BAIT-Tech
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../api';
import { CLASSIFICATIONS } from '../constants/classificationConstants';
import { GAME_MODES } from '../constants/gameModeConstants';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const KO_GAME_MODES = ['SPRINT', 'TANDEM', 'PAARBEWERB'];
const PDF_EXPORT_ORDER = [ 'u10_w', 'u10_m', 'u15_w', 'u15_m', 'u19_w', 'u19_m', 'u23_w', 'u23_m', 'allg_w', 'allg_m', 'damen_classic', 'herren_classic', 'damen_breitensport', 'herren_breitensport', 'ue50_w', 'ue50_m', 'ue60_w', 'ue60_m', 'team_damen_classic', 'team_herren_classic', 'team_mixed_classic', 'team_damen_breitensport', 'team_herren_breitensport', 'team_mixed_breitensport' ];

const ResultsScreen = () => {
  const { id: tournamentId } = useParams();
  const [tournament, setTournament] = useState(null);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getLabel = (key, constantList) => constantList.find(c => c.key === key)?.label || key;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [tournamentRes, scoresRes] = await Promise.all([
          apiClient.get(`/api/tournaments/${tournamentId}`),
          apiClient.get(`/api/tournaments/${tournamentId}/scores`),
        ]);
        setTournament(tournamentRes.data);
        setScores(scoresRes.data.filter(s => s.entry?._id && s.entry?.player));
      } catch (err) { 
        setError("Fehler beim Laden der Ergebnisse."); 
      } finally { 
        setLoading(false); 
      }
    };
    fetchData();
  }, [tournamentId]);

  const resultsByGameMode = useMemo(() => {
    if (!tournament || !scores.length) return {};
    const finalResults = {};
    const gameModesInTournament = tournament.gameModes || ['CLASSIC_120'];
    
    gameModesInTournament.forEach(gameModeKey => {
      const scoresForMode = scores.filter(score => (score.gameMode || 'CLASSIC_120') === gameModeKey);
      if (scoresForMode.length === 0) return;
      
      const singleScores = scoresForMode.filter(s => s.phase === 'qualification' || !s.phase);
      
      const groupedSingles = singleScores.reduce((acc, score) => { 
        const key = score.entry?.classification; 
        if (!key) return acc; 
        if (!acc[key]) acc[key] = []; 
        acc[key].push(score); 
        return acc; 
      }, {});
      
      for (const key in groupedSingles) { 
        groupedSingles[key].sort((a, b) => (b.totalGesamt - a.totalGesamt) || (b.totalAbr - a.totalAbr) || (a.totalFw - b.totalFw)); 
      }
      
      const teamsInMode = singleScores.reduce((acc, score) => { 
        const teamInfo = score.entry?.team; 
        if (!teamInfo) return acc; 
        if (!acc[teamInfo._id]) acc[teamInfo._id] = { ...teamInfo, scores: [] }; 
        acc[teamInfo._id].scores.push(score); 
        return acc; 
      }, {});
      
      const calculatedTeams = Object.values(teamsInMode).map(team => {
        const topScores = team.scores.sort((a, b) => b.totalGesamt - a.totalGesamt).slice(0, 4);
        const totalVolle = topScores.reduce((sum, s) => sum + s.totalVolle, 0);
        const totalGesamt = topScores.reduce((sum, s) => sum + s.totalGesamt, 0);
        const totalAbr = topScores.reduce((sum, s) => sum + s.totalAbr, 0);
        const totalFw = topScores.reduce((sum, s) => sum + s.totalFw, 0);
        const average = topScores.length > 0 ? (totalGesamt / topScores.length).toFixed(2) : 0;
        return { ...team, totalVolle, totalGesamt, totalAbr, totalFw, players: topScores, average };
      }).sort((a, b) => (b.totalGesamt - a.totalGesamt) || (b.totalAbr - a.totalAbr) || (a.totalFw - b.totalFw));
      
      const groupedTeams = calculatedTeams.reduce((acc, team) => { 
        const key = team.classification; 
        if (!key) return acc; 
        if (!acc[key]) acc[key] = []; 
        acc[key].push(team); 
        return acc; 
      }, {});
      
      finalResults[gameModeKey] = { singleResults: groupedSingles, teamResults: groupedTeams };
    });
    return finalResults;
  }, [scores, tournament]);

  const getAffiliationName = (entry) => { 
    if (!entry) return 'Vereinslos'; 
    if (entry.team?.name) return entry.team.name; 
    if (entry.player?.club?.name) return entry.player.club.name; 
    if (entry.player?.guestClubName) return entry.player.guestClubName; 
    return 'Vereinslos'; 
  };
  
  const getSingleScoreColor = (gesamt) => { if (gesamt >= 500) return [0, 0, 255]; if (gesamt >= 450) return [0, 128, 0]; if (gesamt >= 400) return [255, 0, 0]; return [0, 0, 0]; };
  const getTeamAverageColor = (avg) => { if (avg >= 500) return [0, 0, 255]; if (avg >= 450) return [0, 128, 0]; if (avg >= 400) return [255, 0, 0]; return [0, 0, 0]; };
  const getTeamTotalColor = (total) => { if (total >= 2000) return [0, 0, 255]; if (total >= 1800) return [0, 128, 0]; if (total >= 1600) return [255, 0, 0]; return [0, 0, 0]; };
  
  const getScoreColorClass = (value, type) => { 
    let thresholds; 
    if (type === 'team-total') thresholds = { blue: 2000, green: 1800, red: 1600 }; 
    else thresholds = { blue: 500, green: 450, red: 400 }; 
    
    if (value >= thresholds.blue) return 'score-blue'; 
    if (value >= thresholds.green) return 'score-green'; 
    if (value >= thresholds.red) return 'score-red'; 
    return ''; 
  };

  const drawTable = (doc, title, head, body, columnStyles, startY, isTeamTable = false) => {
    let yPos = startY;
    if (yPos > 250) { doc.addPage(); yPos = 15; }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 14, yPos);
    yPos += 7;
    
    autoTable(doc, {
      startY: yPos,
      head: [head],
      body: body,
      theme: 'grid',
      headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: 20, fontStyle: 'bold' },
      columnStyles: columnStyles,
      didParseCell: (data) => {
          if (data.section === 'body' && data.row.index < 3) {
            data.cell.styles.fontStyle = 'bold';
          }
          
          const gesamtIndex = head.indexOf('Gesamt');
          const schnittIndex = head.indexOf('Schnitt');
          
          if (data.column.index === gesamtIndex && data.section === 'body') {
              data.cell.styles.fontStyle = 'bold';
              const value = Number(data.cell.raw);
              data.cell.styles.textColor = isTeamTable ? getTeamTotalColor(value) : getSingleScoreColor(value);
          }
          
          if (isTeamTable && data.column.index === schnittIndex && data.section === 'body') {
            data.cell.styles.fontStyle = 'bold';
            const value = Number(data.cell.raw);
            data.cell.styles.textColor = getTeamAverageColor(value);
          }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.row.index === 11) {
          doc.setLineWidth(1.2); 
          doc.setDrawColor(0, 0, 0); 
          doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
        }
      }
    });
    return doc.lastAutoTable.finalY + 10;
  };

  const exportAllResultsToPDF = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Ergebnisliste: ${tournament.name}`, doc.internal.pageSize.getWidth() / 2, 16, { align: 'center' });
    let yPos = 25;

    const gameModeKeys = Object.keys(resultsByGameMode);
    const hasMultipleGameModes = gameModeKeys.length > 1;

    gameModeKeys.forEach(gameModeKey => {
      const results = resultsByGameMode[gameModeKey];
      
      if (hasMultipleGameModes) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        if (yPos > 25) yPos += 5;
        doc.text(getLabel(gameModeKey, GAME_MODES), 14, yPos);
        yPos += 10;
      }
      
      PDF_EXPORT_ORDER.forEach(key => {
        if (results.teamResults[key]?.length > 0) {
          const teams = results.teamResults[key];
          const head = ['Rang', 'Mannschaft', 'Volle', 'Abräumen', 'Gesamt', 'Schnitt', 'FW'];
          const body = teams.map((team, index) => [`${index + 1}.`, team.name, team.totalVolle, team.totalAbr, team.totalGesamt, team.average, team.totalFw]);
          const styles = { 0: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } };
          yPos = drawTable(doc, getLabel(key, CLASSIFICATIONS), head, body, styles, yPos, true);
        } else if (results.singleResults[key]?.length > 0) {
          const singles = results.singleResults[key];
          const head = ['Rang', 'Name', 'Verein', 'Volle', 'Abr.', 'Gesamt', 'FW'];
          const body = singles.map((score, index) => [`${index + 1}.`, `${score.entry.player.lastName}, ${score.entry.player.firstName}`, getAffiliationName(score.entry), score.totalVolle, score.totalAbr, score.totalGesamt, score.totalFw]);
          const styles = { 0: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } };
          yPos = drawTable(doc, getLabel(key, CLASSIFICATIONS), head, body, styles, yPos, false);
        }
      });
    });
    doc.save(`Ergebnisliste_${tournament.name.replace(/\s/g, '_')}_Gesamt.pdf`);
  };

  // NEU: Funktion, um nur eine spezifische Wertung in ein PDF zu exportieren
  const exportSingleClassificationToPDF = (gameModeKey, classificationKey, isTeam) => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Ergebnisliste: ${tournament.name}`, doc.internal.pageSize.getWidth() / 2, 16, { align: 'center' });
    let yPos = 25;

    const results = resultsByGameMode[gameModeKey];
    if (!results) return;

    // Spielmodus andrucken
    doc.setFontSize(14);
    doc.text(getLabel(gameModeKey, GAME_MODES), 14, yPos);
    yPos += 10;

    const classificationLabel = getLabel(classificationKey, CLASSIFICATIONS);

    if (isTeam) {
      const teams = results.teamResults[classificationKey];
      if (!teams || teams.length === 0) return;
      
      const head = ['Rang', 'Mannschaft', 'Volle', 'Abräumen', 'Gesamt', 'Schnitt', 'FW'];
      const body = teams.map((team, index) => [`${index + 1}.`, team.name, team.totalVolle, team.totalAbr, team.totalGesamt, team.average, team.totalFw]);
      const styles = { 0: { halign: 'center' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } };
      
      drawTable(doc, classificationLabel, head, body, styles, yPos, true);
    } else {
      const singles = results.singleResults[classificationKey];
      if (!singles || singles.length === 0) return;
      
      const head = ['Rang', 'Name', 'Verein', 'Volle', 'Abr.', 'Gesamt', 'FW'];
      const body = singles.map((score, index) => [`${index + 1}.`, `${score.entry.player.lastName}, ${score.entry.player.firstName}`, getAffiliationName(score.entry), score.totalVolle, score.totalAbr, score.totalGesamt, score.totalFw]);
      const styles = { 0: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center' } };
      
      drawTable(doc, classificationLabel, head, body, styles, yPos, false);
    }

    // Sauberen Dateinamen generieren (z.B. "Ergebnis_U19_maennlich.pdf")
    const safeFileName = classificationLabel.replace(/\s/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    doc.save(`Ergebnis_${safeFileName}.pdf`);
  };

  const exportDetailedTeamListPDF = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(`Detaillierte Mannschaftsergebnisse: ${tournament.name}`, doc.internal.pageSize.getWidth() / 2, 16, { align: 'center' });
    let yPos = 25;
    Object.keys(resultsByGameMode).forEach(gameModeKey => {
        const results = resultsByGameMode[gameModeKey];
        const teamClassifications = PDF_EXPORT_ORDER.filter(key => key.startsWith('team_') && results.teamResults[key]?.length > 0);
        if (teamClassifications.length === 0) return;
        if (yPos > 25) { doc.addPage(); yPos = 15; }
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(getLabel(gameModeKey, GAME_MODES), 14, yPos);
        yPos += 10;
        const body = [];
        teamClassifications.forEach(key => {
            body.push([{ content: getLabel(key, CLASSIFICATIONS), colSpan: 7, styles: { halign: 'left', fontStyle: 'bold', fillColor: [230, 230, 230] } }]);
            results.teamResults[key].forEach((team, index) => {
                body.push([ { content: `${index + 1}.`, styles: { fontStyle: 'bold' } }, { content: team.name, styles: { fontStyle: 'bold' } }, { content: team.totalVolle, styles: { fontStyle: 'bold' } }, { content: team.totalAbr, styles: { fontStyle: 'bold' } }, { content: team.totalGesamt, styles: { fontStyle: 'bold' } }, { content: team.average, styles: { fontStyle: 'bold' } }, { content: team.totalFw, styles: { fontStyle: 'bold' } }, ]);
                team.players.forEach(player => body.push(['', `  ${player.entry.player.lastName}, ${player.entry.player.firstName}`, player.totalVolle, player.totalAbr, player.totalGesamt, '', player.totalFw]));
            });
        });
        autoTable(doc, {
            startY: yPos,
            head: [['Rang', 'Mannschaft / Spieler', 'Volle', 'Abr.', 'Gesamt', 'Schnitt', 'FW']],
            body: body,
            theme: 'grid',
            headStyles: { halign: 'center', fillColor: [220, 220, 220], textColor: 20, fontStyle: 'bold' },
            columnStyles: { 0: { halign: 'center', cellWidth: 12 }, 1: { cellWidth: 'auto' }, 2: { halign: 'center' }, 3: { halign: 'center' }, 4: { halign: 'center' }, 5: { halign: 'center' }, 6: { halign: 'center', cellWidth: 12 } },
            didParseCell: (data) => {
                if (data.section !== 'body' || data.row.raw.length <= 1) return;
                const isPlayerRow = !data.row.cells[5].raw && data.row.cells[5].raw !== 0;
                const isTeamRow = !isPlayerRow;
                const gesamtIndex = 4;
                const schnittIndex = 5;
                if (data.column.index === gesamtIndex && data.section === 'body') { 
                  const value = Number(data.cell.raw); 
                  data.cell.styles.textColor = isTeamRow ? getTeamTotalColor(value) : getSingleScoreColor(value); 
                  data.cell.styles.fontStyle = 'bold'; 
                }
                if (data.column.index === schnittIndex && isTeamRow && data.section === 'body') { 
                  const value = Number(data.cell.raw); 
                  data.cell.styles.textColor = getTeamAverageColor(value); 
                }
            },
        });
        yPos = doc.lastAutoTable.finalY + 15;
    });
    doc.save(`Mannschaftsdetails_${tournament.name.replace(/\s/g, '_')}.pdf`);
  };

  if (loading) return <p>Lade Ergebnisse...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="screen-container">
      <div className="navigation-header">
         <Link to={`/turnier/${tournamentId}`} className="btn-back">← Zurück zur Nennungsverwaltung</Link>
         <div>
           <button onClick={exportAllResultsToPDF} className="btn-secondary-action">Gesamtliste (PDF)</button>
           <button onClick={exportDetailedTeamListPDF} className="btn-primary-action">Detaillierte Mannschaftsliste (PDF)</button>
         </div>
      </div>
      <h2>Ergebnisse für {tournament?.name}</h2>
      
      {Object.keys(resultsByGameMode).length > 0 ? (
        Object.keys(resultsByGameMode).map(gameModeKey => (
          <div key={gameModeKey} className="gamemode-result-section" style={{marginBottom: '3rem'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #007bff', paddingBottom: '0.5rem'}}>
              <h2>{getLabel(gameModeKey, GAME_MODES)}</h2>
              {KO_GAME_MODES.includes(gameModeKey) && (
                <Link to={`/turnier/${tournamentId}/ko-manager/${gameModeKey}`} className="btn-primary-action">KO-Phasen verwalten</Link>
              )}
            </div>
            
            {/* MANNSCHAFTS-WERTUNGEN RENDERN */}
            {PDF_EXPORT_ORDER.filter(key => resultsByGameMode[gameModeKey].teamResults[key]?.length > 0).map(classificationKey => (
              <div key={`team-${classificationKey}`} className="result-group">
                <div className="result-group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>{getLabel(classificationKey, CLASSIFICATIONS)}</h4>
                  {/* Container für die neuen Buttons */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => exportSingleClassificationToPDF(gameModeKey, classificationKey, true)}
                      className="btn-primary-action" 
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', border: 'none', cursor: 'pointer' }}
                    >
                      🖨️ Drucken (PDF)
                    </button>
                    <a 
                      href={`/turnier/${tournamentId}/stream/${classificationKey}?gameMode=${gameModeKey}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-secondary-action" 
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', textDecoration: 'none' }}
                    >
                      📺 Stream/TV Ansicht
                    </a>
                  </div>
                </div>
                <table>
                  <thead>
                    <tr><th>Rang</th><th>Mannschaft</th><th>Volle</th><th>Abr.</th><th>Gesamt</th><th>Schnitt</th><th>FW</th></tr>
                  </thead>
                  <tbody>
                    {resultsByGameMode[gameModeKey].teamResults[classificationKey].map((team, index) => (
                      <tr key={team.name} style={index < 3 ? { fontWeight: 'bold' } : {}}>
                        <td>{index + 1}</td>
                        <td><strong>{team.name}</strong><ul className="player-sublist">{team.players.map(p => <li key={p._id} style={{fontWeight: 'normal'}}>{p.entry.player.lastName}, {p.entry.player.firstName} - {p.totalGesamt}</li>)}</ul></td>
                        <td>{team.totalVolle}</td>
                        <td>{team.totalAbr}</td>
                        <td className={getScoreColorClass(team.totalGesamt, 'team-total')}><strong>{team.totalGesamt}</strong></td>
                        <td className={getScoreColorClass(team.average, 'team-average')}><strong>{team.average}</strong></td>
                        <td>{team.totalFw}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            
            {/* EINZEL-WERTUNGEN RENDERN */}
            {PDF_EXPORT_ORDER.filter(key => resultsByGameMode[gameModeKey].singleResults[key]?.length > 0).map(classificationKey => (
              <div key={classificationKey} className="result-group">
                <div className="result-group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h4>{getLabel(classificationKey, CLASSIFICATIONS)}</h4>
                  {/* Container für die neuen Buttons */}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => exportSingleClassificationToPDF(gameModeKey, classificationKey, false)}
                      className="btn-primary-action" 
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', border: 'none', cursor: 'pointer' }}
                    >
                      🖨️ Drucken (PDF)
                    </button>
                    <a 
                      href={`/turnier/${tournamentId}/stream/${classificationKey}?gameMode=${gameModeKey}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-secondary-action" 
                      style={{ fontSize: '0.85rem', padding: '0.3rem 0.6rem', textDecoration: 'none' }}
                    >
                      📺 Stream/TV Ansicht
                    </a>
                  </div>
                </div>
                <table>
                  <thead>
                    <tr><th>Rang</th><th>Name</th><th>Verein / Mannschaft</th><th>Volle</th><th>Abräumen</th><th>Gesamt</th><th>FW</th></tr>
                  </thead>
                  <tbody>
                    {resultsByGameMode[gameModeKey].singleResults[classificationKey].map((score, index) => (
                      <tr key={score._id} style={index < 3 ? { fontWeight: 'bold' } : {}}>
                        <td>{index + 1}</td>
                        <td>{score.entry.player.lastName}, {score.entry.player.firstName}</td>
                        <td>{getAffiliationName(score.entry)}</td>
                        <td>{score.totalVolle}</td>
                        <td>{score.totalAbr}</td>
                        <td className={getScoreColorClass(score.totalGesamt, 'single')}><strong>{score.totalGesamt}</strong></td>
                        <td>{score.totalFw}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ))
      ) : (
        <p>Noch keine Ergebnisse für dieses Turnier erfasst.</p>
      )}
    </div>
  );
};
export default ResultsScreen;